import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Lock, Check } from 'lucide-react';
import { track } from '../utils/analytics';

const NAVY = '#0d1b3e';
const GOLD = '#c9a227';
const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001') + '/api';

// Reusable paywall overlay. Wrap around a preview of a locked feature, or use
// as a full-page lock by passing fullPage=true. Matches landing page brand
// (navy/gold, Playfair Display).
//
// Props:
//   title: string, headline
//   body: string, sub-copy
//   features: string[], bullet list of what they unlock
//   recommendedTier: 'starter' | 'dealer' | 'pro', which tier to upsell
//   children: preview content to blur behind the overlay (when !fullPage)
//   fullPage: if true, renders as a full-page lock instead of overlay
export default function Paywall({
  title = 'Unlock with a Paid Plan',
  body = 'This feature is available on Starter and up. Upgrade now, cancel anytime.',
  features = [],
  recommendedTier = 'dealer',
  children = null,
  fullPage = false,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [discountEligible, setDiscountEligible] = useState(false);

  useEffect(() => {
    track('paywall_viewed', { tier: recommendedTier, title, fullPage });
  }, [recommendedTier, title, fullPage]);

  // Show the unlocked 50%-off first month if this user is eligible (survey /
  // referral / post-trial). The coupon already applies server-side at checkout;
  // this just surfaces it so an eligible user doesn't see full price and bail.
  useEffect(() => {
    fetch(`${API}/stripe/subscription`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => r.json())
      .then(d => setDiscountEligible(!!d.discountEligible))
      .catch(() => {});
  }, []);

  const checkout = async (tier) => {
    track('paywall_upgrade_clicked', { tier, title });
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/stripe/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ tier, source: 'paywall' }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else {
        setError(data.error || 'Could not start checkout. Please try again or contact support.');
        setLoading(false);
      }
    } catch (err) {
      setError('Network error. Check your connection and try again.');
      setLoading(false);
    }
  };

  const tierInfo = {
    starter:  { name: 'Starter',    price: '$19.99', first: '$9.99' },
    dealer:   { name: 'Dealer',     price: '$59.99', first: '$29.99' },
    pro:      { name: 'Pro Dealer', price: '$89.99', first: '$44.99' },
  }[recommendedTier] || { name: 'Dealer', price: '$59.99', first: '$29.99' };

  const card = (
    <div style={{
      background: 'white',
      borderRadius: 16,
      padding: '32px 30px',
      maxWidth: 460,
      width: '100%',
      boxShadow: '0 20px 60px rgba(13,27,62,0.2)',
      border: `1px solid rgba(201,162,39,0.3)`,
      fontFamily: "'Manrope', sans-serif",
      textAlign: 'center',
    }}>
      {/* Lock icon */}
      <div style={{
        width: 60, height: 60, borderRadius: 14,
        background: NAVY,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 18px',
        border: `2px solid rgba(201,162,39,0.4)`,
      }}>
        <Lock size={26} color={GOLD} strokeWidth={2.25} />
      </div>

      {/* Tier badge */}
      <div style={{ display: 'inline-block', background: 'rgba(201,162,39,0.1)', color: GOLD, fontSize: 10, fontWeight: 800, letterSpacing: '0.12em', padding: '4px 12px', borderRadius: 100, marginBottom: 14, border: '1px solid rgba(201,162,39,0.25)' }}>
        {tierInfo.name.toUpperCase()} & UP
      </div>

      {/* Headline */}
      <h2 style={{
        fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800,
        color: NAVY, letterSpacing: '-0.6px', lineHeight: 1.15,
        margin: '0 0 12px',
      }}>{title}</h2>

      {/* Body */}
      <p style={{ fontSize: 14, color: '#5b6478', lineHeight: 1.6, margin: '0 0 22px' }}>
        {body}
      </p>

      {/* Features */}
      {features.length > 0 && (
        <div style={{ background: '#fafbff', borderRadius: 12, padding: '14px 16px', marginBottom: 22, border: '1px solid #eef0ff', textAlign: 'left' }}>
          {features.map((f, i) => (
            <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '5px 0', borderTop: i > 0 ? '1px solid #eef0ff' : 'none' }}>
              <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'rgba(22,163,74,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                <Check size={10} color="#16a34a" strokeWidth={3.5} />
              </div>
              <span style={{ fontSize: 13, color: '#4a5568', fontWeight: 500, lineHeight: 1.45 }}>{f}</span>
            </div>
          ))}
        </div>
      )}

      {/* Inline error if checkout fails, replaces native alert() */}
      {error && (
        <div style={{
          padding: '10px 14px', marginBottom: 10,
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
          fontSize: 12, color: '#991b1b', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
        }}>
          <span>{error}</span>
          <button
            onClick={() => { setError(null); checkout(recommendedTier); }}
            style={{ background: 'none', border: '1px solid #991b1b', color: '#991b1b', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Unlocked-discount banner, only for eligible users */}
      {discountEligible && (
        <div style={{ background: '#eef0ff', border: '1px solid #c7d2fe', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 13, fontWeight: 700, color: '#1a36d5' }}>
          🎉 You've unlocked <strong>50% off your first month</strong>
        </div>
      )}

      {/* Primary CTA */}
      <button
        onClick={() => checkout(recommendedTier)}
        disabled={loading}
        style={{
          width: '100%', padding: '14px 24px',
          background: GOLD, color: NAVY, border: 'none', borderRadius: 10,
          fontSize: 15, fontWeight: 800, cursor: loading ? 'wait' : 'pointer',
          fontFamily: "'Manrope', sans-serif", letterSpacing: '0.01em',
          marginBottom: discountEligible ? 4 : 10, boxShadow: '0 4px 14px rgba(201,162,39,0.25)',
          transition: 'transform 0.15s',
        }}
        onMouseEnter={e => { if (!loading) e.currentTarget.style.transform = 'translateY(-1px)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
      >
        {loading ? 'Loading…' : (discountEligible
          ? `Upgrade to ${tierInfo.name}, ${tierInfo.first} first month`
          : `Upgrade to ${tierInfo.name}, ${tierInfo.price}/mo`)}
      </button>
      {discountEligible && (
        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 10 }}>then {tierInfo.price}/mo · cancel anytime</div>
      )}

      {/* Secondary link */}
      <div>
        <Link
          to="/settings?tab=subscription"
          style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 3 }}
        >
          See all plans →
        </Link>
      </div>
    </div>
  );

  if (fullPage) {
    return (
      <div style={{ minHeight: 'calc(100vh - 60px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', background: '#f5f6fa' }}>
        {card}
      </div>
    );
  }

  // Overlay mode, wrap around preview content
  return (
    <div style={{ position: 'relative', minHeight: 400 }}>
      {/* Blurred preview behind */}
      <div style={{ filter: 'blur(6px) saturate(0.85)', opacity: 0.6, pointerEvents: 'none', userSelect: 'none' }}>
        {children}
      </div>
      {/* Gradient fade */}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(245,246,250,0.3) 0%, rgba(245,246,250,0.95) 60%)' }} />
      {/* Card centered */}
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        {card}
      </div>
    </div>
  );
}
